<script lang="ts">
    import TrainingCard from "./TrainingCard.svelte";
    import type { SPFocus } from "../../Util/api";
    import type { MyFitnessStores } from "../stores";
    import { FocusTypes, type ForecastActivityState, ForecastType, SpecificityTypes } from '@/Common/constants';
    import { differenceInDays, differenceInWeeks, endOfDay, fromUnixTime, startOfDay } from 'date-fns';

    export let isModal: boolean = false
    export let myFitness: MyFitnessStores
    export let forecast_activities
    export let training_status_collection
    export let target_event
    export let start_forecast
    export let activities_3day: any[][] | null = null
    export let spFocus_3day: SPFocus[] | null = null
    export let isLoading: boolean = false
    export let onCardClick: (activity: any) => void = () => {}
    export let selectedActivityIndex: number = 1
    export let lastBTDate: Date | null = null

    let { trainingAdvice, trainingPlan } = myFitness

    $: phase = $trainingAdvice.phase

    const extractForecastType = (state: string[]) => {
        if (state.includes('rest')) {
            return ForecastType.Rest
        } else if (state.includes('recovery')) {
            return ForecastType.Recovery
        } else if (state.includes('high_intensity')) {
            return ForecastType.HighIntensity
        } else if (state.includes('low_intensity')) {
            return ForecastType.LowIntensity
        } else if (state.includes('event_A')) {
            return ForecastType.Event
        } else {
            console.warn(state)
            throw new Error('Unknown forecast state')
        }
    }

    //For bind only
    export let forecastInfo: ForecastActivityState | null = null

    $: if (
            $trainingPlan?.forecastResults?.days && forecast_activities &&
            (selectedActivityIndex || selectedActivityIndex === 0) &&
            selectedActivityIndex >= 0 && 
            selectedActivityIndex < forecast_activities.length &&
            forecast_activities?.at(selectedActivityIndex)?.state?.includes('forecast') && !forecast_activities?.at(selectedActivityIndex)?.state?.includes('completed') &&
            forecast_activities?.at(selectedActivityIndex).training_plan_id === ($trainingPlan.training_plan_id ?? $trainingPlan._id)
        ) {

        const activity = forecast_activities[selectedActivityIndex]
        const days = $trainingPlan.forecastResults.days

        if ((!activity.forecast_index && activity.forecast_index !== 0) || activity.forecast_index >= days.length) {
            console.warn(activity, days)
            throw new Error('No forecast index / invalid forecast index')
        }
        
        const todayState = extractForecastType(days.at(activity.forecast_index).state)

        let tommorowState: ForecastType | null = null
        if (activity.forecast_index + 1 < days.length) {
            const tommorowDay = days.at(activity.forecast_index + 1)
            tommorowState = extractForecastType(tommorowDay.state)
        }
        
        let specificity: SpecificityTypes | null = null
        switch ((activity.spr ?? activity.specRating)?.toLowerCase()) {
            case 'pure':
                specificity = SpecificityTypes.Pure
                break
            case 'mixed':
                specificity = SpecificityTypes.Mixed
                break
            case 'polar':
                specificity = SpecificityTypes.Polar
                break
            default:
                console.warn('Unknown specificity. Using Pure as default', activity)
                specificity = SpecificityTypes.Pure
        }

        let focus: FocusTypes | null = null
        switch ((activity.focus ?? activity.fa)?.toLowerCase()) {
            case 'road sprinter':
                focus = FocusTypes.RoadSprinter
                break
            case 'pursuiter':
                focus = FocusTypes.Pursuiter
                break
            case 'puncheur':
                focus = FocusTypes.Puncheur
                break
            case 'breakaway specialist':
                focus = FocusTypes.BreakawaySpecialist
                break
            case 'rouleur':
                focus = FocusTypes.Rouleur
                break
            case 'gc specialist':
                focus = FocusTypes.GcSpecialist
                break
            case 'climber':
                focus = FocusTypes.Climber
                break
            case 'endurance':
                focus = FocusTypes.Endurance
                break
            default:
                console.warn(activity, 'Unknown focus. Using endurance as default')
                // throw new Error('Unknown focus')
                focus = FocusTypes.Endurance
                break
        }

        let daysToHighIntensity: number = -1
        let weeksToEvent: number = -1
        for (let i = activity.forecast_index + 1; i < days.length; i++) {
            if (daysToHighIntensity < 0 && days[i].state?.includes('high_intensity')) {
                const nextHIDate = fromUnixTime(days[i].t)
                daysToHighIntensity = differenceInDays(nextHIDate, startOfDay(new Date(activity.start_date)))
            }
            if (weeksToEvent < 0 && days[i].state?.includes('event_A')) {
                const nextEventDate = fromUnixTime(days[i].t)
                weeksToEvent = differenceInWeeks(nextEventDate, startOfDay(new Date(activity.start_date)))
            }
        }

        if ($trainingPlan.selected_mode == 'target_event') {
            weeksToEvent = 9999
        }

        const daysSinceBT = lastBTDate ? differenceInDays(endOfDay(new Date(activity.start_date)), lastBTDate) : 99999
        const polarizationLevel = $trainingPlan.settings?.polarization ?? 4

        forecastInfo = {
            type: todayState,
            nextDayType: tommorowState,
            specificity,
            focus,
            daysToHighIntensity,
            weeksToEvent,
            daysSinceBT,
            polarizationLevel,
            isChallenging: true,
            currentSignature: spFocus_3day.at(selectedActivityIndex).sig,
            activitySignature: activity.signature,
            duration: activity.duration,
            targets: {xss: activity.xss, xlss: activity.xlss, xhss: activity.xhss, xpss: activity.xpss},
            path: activity.path,
        }

    } else {
        forecastInfo = null
    }
</script>

<div class="wrapper">
    <!-- <TrainingInfo {isModal} {target_event} {phase} start_forecast={start_forecast}/> --> <!-- NOTE: Removed Training Info since data is duplicated on the program page -->
    <div class='training-cards-container' class:single-card={isModal} class:isLoading>
        {#if isLoading}
        <i style="color: var(--color-xert-teal); font-size: 32px; flex: 0 0 0;" class="fas fa-circle-notch fa-spin absoluteSpinner" />
        {/if}
        {#if forecast_activities}
            {#each forecast_activities as activity, i(i)}
                {#if !isModal || i==1}
                    <TrainingCard index={i} {isModal} {myFitness} onClickCallback={onCardClick} {activity} training_status_array={training_status_collection[i]} activities={activities_3day?.at(i)} spFocus={spFocus_3day?.at(i)} />
                {/if}
            {/each}
        {/if}
    </div>
    <!-- {#if training_status_collection && !isModal}
        <div class="gradient-wrapper">
            <TrainingStatusGradient training_status_array={training_status_collection.flat()} height={8}/>
        </div>
    {/if} -->
</div>

<style lang="scss">
    .wrapper {
        height: max-content;
        width: 100%;
        max-width: 100%;
        margin: 0;
        padding: 0 0 15px;
        container: cards-container / inline-size;
        position: relative;
        .training-cards-container {
            display: grid;
            grid-template-rows: repeat(3, minmax(0, 1fr));
            gap: 4px;
            margin: 24px 0 0;
            max-width: 100%;
            width: 100%;
            margin: 0 auto;
            &.isLoading {
                filter: brightness(0.75);
                background: white;
                .absoluteSpinner {
                    position: absolute;
                    top: 50%;
                    left: calc(50% - 16px);
                    transform: translate(-50%, -50%);
                    color: #016e79;
                    z-index: 2;
                }
            }
            &.single-card {
                grid-template-rows: 1fr;
                grid-template-columns: 1fr;
                width: auto;
                max-width: 599px;
            }
        }
        .gradient-wrapper {
            display: none;
            width: 100%;
        }
        @mixin column-format {
            padding: 0 20px 15px;
            .training-cards-container {
                grid-template-rows: 1fr;
                grid-template-columns: repeat(3, minmax(0, 1fr));
                width: auto;
                &.single-card {
                    grid-template-rows: 1fr;
                    grid-template-columns: 1fr;
                    max-width: 599px;
                }
            }
            .gradient-wrapper {
                margin: 8px 0 0;
                display: block
            }
        }
        // breakpoints should be the same as the ones in TrainingCard.svelte
        @supports (contain: inline-size) {
            // container is in ForecastTraining.svelte
            @container cards-container (min-width: 600px) {
                @include column-format;
            }
        }
        @supports not (contain: inline-size) {
            @media (min-width: 1860px), (max-width: 993px) and (min-width: 790px) {
                @include column-format;
            }
        }
    }
</style>
